import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Phone, AlertTriangle, CheckCircle, MessageSquare, User } from 'lucide-react';
import api from '@/lib/api';

const categoryColor = (cat) => ({
  compensation: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  benefits:     'bg-blue-50 text-blue-700 border-blue-200',
  process:      'bg-violet-50 text-violet-700 border-violet-200',
  role_detail:  'bg-amber-50 text-amber-700 border-amber-200',
  other:        'bg-slate-100 text-slate-600 border-slate-200',
}[cat] || 'bg-slate-100 text-slate-600 border-slate-200');

const noteTypeLabel = (t) =>
  t === 'reschedule_request' ? 'Reschedule Requested' : 'Human Recruiter Requested';

export default function VoiceCallDetail() {
  const { id }     = useParams();
  const navigate   = useNavigate();
  const workspaceId = localStorage.getItem('vocala_workspace_id') || '';

  const [session,   setSession]   = useState(null);
  const [scheduled, setScheduled] = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState('');

  useEffect(() => {
    if (!id) return;
    const load = async () => {
      setLoading(true);
      try {
        let loadedFromDetail = false;
        try {
          const detailRes = await api.get(`/voice/calls/${id}`, {
            params: { workspace_id: workspaceId },
          });
          if (detailRes?.data) {
            setSession(detailRes.data);
            loadedFromDetail = true;
          }
        } catch {
          loadedFromDetail = false;
        }

        if (!loadedFromDetail) {
          // Fallback for older deployments
          const [liveRes, schedRes] = await Promise.allSettled([
            api.get(`/voice/live-calls`, { params: { workspace_id: workspaceId } }),
            api.get(`/voice/scheduled-calls`, { params: { workspace_id: workspaceId } }),
          ]);
          if (liveRes.status === 'fulfilled') {
            const s = (liveRes.value.data || []).find(c =>
              c.call_session_id === id || c.callsessionid === id
            );
            if (s) setSession(s);
          }
          if (schedRes.status === 'fulfilled') {
            const sc = (schedRes.value.data || []).find(c => c.id === id);
            if (sc) setScheduled(sc);
          }
        }
      } catch {
        setError('Unable to load call details.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, workspaceId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return (
    <div className="flex-1 flex items-center justify-center">
      <div className="w-8 h-8 rounded-full bg-blue-500 animate-pulse" />
    </div>
  );

  const data = session || scheduled;
  if (!data && !loading) return (
    <div className="flex-1 p-8">
      <p className="text-slate-500">{error || 'Call not found.'}</p>
    </div>
  );

  const unanswered  = session?.unanswered_questions  || [];
  const notes       = scheduled?.candidate_notes     || session?.candidate_notes || [];
  const brainALog   = session?.brain_a_log || [];
  const strikes     = session?.strikes ?? 0;
  const techScore   = session?.running_tech_score ?? session?.live_scores?.technical ?? null;
  const status      = data?.status || '—';

  return (
    <div className="flex-1 overflow-auto bg-transparent p-6 sm:p-8" style={{ fontFamily: 'Inter, sans-serif' }}>

      {/* Back */}
      <button onClick={() => navigate('/voice')}
        className="inline-flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-blue-600 bg-white/50 backdrop-blur-md px-4 py-2 rounded-xl border border-slate-200/50 hover:shadow-md transition-all mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to Voice Screening
      </button>

      {/* Header card */}
      <div className="bg-white/90 backdrop-blur-xl border border-slate-200/60 rounded-3xl shadow-xl p-6 mb-6 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-bl from-blue-100/40 to-transparent rounded-full blur-3xl pointer-events-none -z-10" />
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-100 to-emerald-50 border-2 border-white shadow flex items-center justify-center text-blue-700 font-bold text-lg">
            {(data?.candidate_name || data?.phone || 'C').charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl font-black text-slate-900 tracking-tight">
              {data?.candidate_name || data?.phone || 'Unknown Candidate'}
            </h1>
            <p className="text-slate-500 text-sm mt-0.5">{data?.job_title || data?.role_name || '—'}</p>
          </div>
          <span className={`px-3 py-1 rounded-full text-xs font-bold border ${
            status === 'completed'  ? 'bg-emerald-100 text-emerald-700 border-emerald-200' :
            status === 'handoff'    ? 'bg-purple-100 text-purple-700 border-purple-200' :
            status === 'active'     ? 'bg-blue-100 text-blue-700 border-blue-200' :
            status === 'failed'     ? 'bg-red-100 text-red-700 border-red-200' :
                                      'bg-slate-100 text-slate-600 border-slate-200'}`}>
            {status}
          </span>
        </div>

        {/* Score summary */}
        <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-4">
          {[
            { label: 'Proctoring Strikes', val: `${strikes}/3`, warn: strikes >= 2 },
            { label: 'AI Score',           val: techScore != null ? `${techScore}/100` : '—' },
            { label: 'Attempts',           val: `${data?.attempts ?? 0}/3` },
          ].map(({ label, val, warn }) => (
            <div key={label} className={`rounded-xl p-4 border ${warn ? 'bg-red-50 border-red-200' : 'bg-slate-50/80 border-slate-200/50'}`}>
              <p className={`text-lg font-black ${warn ? 'text-red-700' : 'text-slate-900'}`}>{val}</p>
              <p className="text-xs text-slate-500 mt-0.5">{label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Unanswered questions */}
      {unanswered.length > 0 && (
        <div className="bg-white/80 backdrop-blur-sm border border-slate-200/50 rounded-2xl p-6 shadow-lg mb-5">
          <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2 mb-4">
            <MessageSquare className="w-4 h-4 text-violet-500" />
            Questions for Human Follow-up
          </h3>
          <ul className="space-y-3">
            {unanswered.map((q, idx) => (
              <li key={q.id || q.timestamp || idx} className="rounded-xl border border-slate-100 bg-slate-50/80 px-4 py-3">
                <div className="flex items-center justify-between mb-1.5">
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-bold border ${categoryColor(q.category)}`}>
                    {q.category}
                  </span>
                  <span className="text-[11px] text-slate-400">
                    {new Date(q.timestamp).toLocaleString()}
                  </span>
                </div>
                <p className="text-sm text-slate-700">{q.text}</p>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Candidate notes */}
      {notes.length > 0 && (
        <div className="bg-white/80 backdrop-blur-sm border border-amber-200/50 rounded-2xl p-6 shadow-lg mb-5">
          <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2 mb-4">
            <User className="w-4 h-4 text-amber-500" />
            Candidate Notes
          </h3>
          <ul className="space-y-3">
            {notes.map((n, idx) => (
              <li key={n.id || n.timestamp || idx} className="rounded-xl border border-amber-100 bg-amber-50/60 px-4 py-3">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="inline-flex px-2 py-0.5 rounded-full text-[11px] font-bold bg-amber-100 text-amber-700 border border-amber-200">
                    {noteTypeLabel(n.type)}
                  </span>
                  <span className="text-[11px] text-slate-400">
                    {new Date(n.timestamp).toLocaleString()}
                  </span>
                </div>
                <p className="text-sm text-slate-700">"{n.text}"</p>
              </li>
            ))}
          </ul>
        </div>
      )}

      {brainALog.length > 0 && (
        <section className="mt-6 mb-5" data-testid="brain-a-interview-flow-section">
          <h3 className="text-sm font-semibold text-slate-800" data-testid="brain-a-interview-flow-title">
            Interview Flow (Brain A Decisions)
          </h3>
          <p className="mt-1 text-xs text-slate-500" data-testid="brain-a-interview-flow-description">
            Shows why the AI interviewer probed deeper, moved on, or flagged gaps.
          </p>
          <ol className="mt-3 space-y-2 text-sm text-slate-700" data-testid="brain-a-interview-flow-list">
            {brainALog.map((entry, idx) => (
              <li
                key={entry.id || entry.timestamp || idx}
                className="rounded-md border border-slate-100 bg-slate-50 px-3 py-2"
                data-testid={`brain-a-interview-flow-entry-${idx}`}
              >
                <div className="flex items-center justify-between">
                  <div className="text-[11px] font-semibold text-slate-600">
                    Turn {(entry?.turn ?? idx) + 1} · {entry?.assessment || 'unknown'}
                    {entry?.probe_used && (
                      <span className="ml-2 inline-flex rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700">
                        Probe
                      </span>
                    )}
                    {entry?.is_gap_question && (
                      <span className="ml-2 inline-flex rounded-full bg-rose-50 px-2 py-0.5 text-[10px] font-medium text-rose-700">
                        Gap skill
                      </span>
                    )}
                  </div>
                  <span className="text-[11px] text-slate-400">
                    {entry?.timestamp ? new Date(entry.timestamp).toLocaleString() : ''}
                  </span>
                </div>
                <p className="mt-1 text-xs text-slate-500">
                  Q: {entry?.question_asked || ''}
                </p>
                {entry?.candidate_answer_snippet && (
                  <p className="mt-1 text-xs text-slate-500">
                    A: {entry.candidate_answer_snippet}
                  </p>
                )}
                {entry?.competency_tag && (
                  <p className="mt-1 text-[11px] text-slate-500">
                    Competency: {entry.competency_tag}
                    {entry?.confidence_score != null ? ` · Confidence: ${entry.confidence_score}` : ''}
                  </p>
                )}
              </li>
            ))}
          </ol>
        </section>
      )}

      {unanswered.length === 0 && notes.length === 0 && brainALog.length === 0 && (
        <div className="bg-white/60 border border-slate-200/50 rounded-2xl p-10 text-center">
          <CheckCircle className="w-8 h-8 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 font-medium">No follow-up items for this call.</p>
        </div>
      )}
    </div>
  );
}
